import { useState } from 'react'
import type { Invite } from '../hooks/useInvites'
import { safeExternalUrl } from '../lib/safeUrl'

interface InviteDetailProps {
  invite: Invite
  onRespond: (invite: Invite, action: 'accept' | 'ignore') => Promise<string>
}

export default function InviteDetail({ invite, onRespond }: InviteDetailProps) {
  const [responded, setResponded] = useState(false)
  const [responding, setResponding] = useState(false)
  const profileUrl = safeExternalUrl(invite.profileUrl)

  const handleRespond = async (action: 'accept' | 'ignore') => {
    setResponding(true)
    try {
      await onRespond(invite, action)
      setResponded(true)
    } catch {
      // API failed — buttons stay visible for retry
    } finally {
      setResponding(false)
    }
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
      {/* Profile header */}
      <div className="flex flex-col items-center border-b border-zinc-100 px-8 py-8">
        {invite.profileImage ? (
          <img src={invite.profileImage} alt="" className="h-16 w-16 rounded-full" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-200 text-lg font-semibold text-zinc-500">
            {getInitials(invite.name)}
          </div>
        )}
        <h2 className="mt-3 text-base font-semibold text-zinc-900">{invite.name}</h2>
        {invite.headline && (
          <p className="mt-0.5 text-center text-xs text-zinc-500">{invite.headline}</p>
        )}
        {profileUrl && (
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 text-[11px] text-blue-600 hover:text-blue-700"
          >
            View LinkedIn profile
          </a>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {invite.message && (
          <div className="mb-4 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
            <p className="mb-1 text-[11px] font-medium text-zinc-500">Invitation message</p>
            <p className="whitespace-pre-wrap text-sm text-zinc-700">{invite.message}</p>
          </div>
        )}

        {invite.mutualConnections && (
          <div className="mb-4 rounded-lg bg-zinc-50 px-4 py-3">
            <p className="text-[11px] font-medium text-zinc-500">Mutual connections</p>
            <p className="mt-0.5 text-xs text-zinc-700">{invite.mutualConnections}</p>
          </div>
        )}

        {invite.sentTimeLabel && (
          <p className="text-[11px] text-zinc-400">
            Invitation sent {invite.sentTimeLabel.toLowerCase()}
          </p>
        )}
      </div>

      {/* Action buttons */}
      {!responded && (
        <div className="border-t border-zinc-100 px-8 py-4">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleRespond('accept')}
              disabled={responding}
              className="w-full rounded-lg bg-zinc-900 py-2.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              {responding ? 'Responding...' : 'Accept invitation'}
            </button>
            <button
              onClick={() => handleRespond('ignore')}
              disabled={responding}
              className="w-full rounded-lg border border-zinc-200 py-2.5 text-sm font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
            >
              Ignore
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
}
